
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { 
  AlertCircle, 
  Mail, 
  Loader2, 
  User, 
  UserCog, 
  RefreshCw 
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { invalidateQueryCache } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";

export const AccountSettings = ({ 
  profile, 
  socialLinks, 
  hideAccountRole = false 
}: { 
  profile: any, 
  socialLinks?: Record<string, string> | null, 
  hideAccountRole?: boolean 
}) => {
  const [resetPasswordLoading, setResetPasswordLoading] = useState(false);
  const [fullName, setFullName] = useState(profile?.full_name || "");
  const [bio, setBio] = useState(profile?.bio || "");
  const [avatarUrl, setAvatarUrl] = useState(profile?.avatar_url || null);
  const [avatarFile, setAvatarFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [roleSwitchLoading, setRoleSwitchLoading] = useState(false);
  const [showRoleSwitchDialog, setShowRoleSwitchDialog] = useState(false);
  
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  
  const { data: userData, isLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      return user;
    },
    staleTime: Infinity,
  });

  const handleResetPassword = async () => {
    if (!userData?.email) return;
    
    try {
      setResetPasswordLoading(true);
      
      const { error } = await supabase.auth.resetPasswordForEmail(userData.email, {
        redirectTo: `${window.location.origin}/dashboard/settings`,
      });
      
      if (error) throw error;
      
      toast.success("Password reset email sent. Please check your email for instructions.");
    } catch (error) {
      toast.error(error.message || "Failed to send password reset email. Please try again.");
    } finally {
      setResetPasswordLoading(false);
    }
  };

  const handleAvatarChange = (file) => {
    if (file) {
      setAvatarFile(file);
      const tempUrl = URL.createObjectURL(file);
      setAvatarUrl(tempUrl);
    }
  };

  const uploadFile = async (file, path) => {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Math.random().toString(36).substring(2, 15)}.${fileExt}`;
    const filePath = `${path}/${fileName}`;

    const { data, error } = await supabase.storage
      .from('public-profile')
      .upload(filePath, file);

    if (error) {
      throw error;
    }

    const { data: urlData } = supabase.storage
      .from('public-profile')
      .getPublicUrl(filePath);

    return urlData.publicUrl;
  };

  const saveProfile = async () => {
    try {
      setSaving(true);

      if (!userData?.id) {
        toast.error("User ID not found");
        return;
      }

      let newAvatarUrl = avatarUrl;
      if (avatarFile) {
        newAvatarUrl = await uploadFile(avatarFile, 'avatars');
      }

      const { error } = await supabase
        .from("profiles")
        .update({
          full_name: fullName,
          avatar_url: newAvatarUrl
        })
        .eq("id", userData.id);

      if (error) {
        throw error;
      }
      
      setAvatarFile(null);

      invalidateQueryCache(queryClient, [
        ['userProfile', userData.id],
        ['userProfileRole', userData.id],
        ['userProfileComplete', userData.id],
        'currentUser'
      ]);

      toast.success("Profile updated successfully");
    } catch (error) {
      console.error("Error saving profile:", error);
      toast.error("Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleRoleSwitch = async () => {
    try {
      setRoleSwitchLoading(true);
      
      if (!profile || !userData?.id) {
        toast.error("User profile not found");
        return;
      }
      
      const newRole = profile.role === 'student' ? 'creator' : 'student';
      
      console.log(`Switching role from ${profile.role} to ${newRole}`);
      
      const { error } = await supabase
        .from("profiles")
        .update({ role: newRole })
        .eq("id", userData.id);
        
      if (error) {
        throw error;
      }
      
      invalidateQueryCache(queryClient, [
        ['userProfile', userData.id],
        ['userProfileRole', userData.id],
        ['userProfileComplete', userData.id],
        'currentUser'
      ]);
      
      toast.success(`Your account is now a ${newRole} account.`);
      
      if (newRole === 'creator') {
        if (!profile.username) {
          navigate('/onboarding/username');
        } else {
          navigate('/dashboard/creator');
        }
      } else {
        navigate('/dashboard/student');
      }
      
    } catch (error) {
      console.error("Error switching role:", error);
      toast.error("Failed to switch role. Please try again.");
    } finally {
      setRoleSwitchLoading(false);
      setShowRoleSwitchDialog(false);
    }
  };

  if (isLoading) {
    return <div className="flex justify-center p-8"><Loader2 className="animate-spin h-8 w-8 text-muted-foreground" /></div>;
  }

  const targetRole = profile?.role === 'student' ? 'creator' : 'student';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Profile Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col md:flex-row gap-6 items-start">
            <div className="mx-auto md:mx-0">
              <div className="relative">
                <Avatar className="w-32 h-32 border-4 border-background">
                  <AvatarImage src={avatarUrl || undefined} />
                  <AvatarFallback className="text-3xl">{fullName ? fullName[0].toUpperCase() : 'U'}</AvatarFallback>
                </Avatar>
                <label 
                  htmlFor="avatar-upload" 
                  className="absolute bottom-2 right-2 bg-primary text-primary-foreground p-2 rounded-full cursor-pointer hover:bg-primary/90 transition-colors shadow-md"
                >
                  <User size={16} />
                </label>
                <input 
                  id="avatar-upload" 
                  type="file" 
                  accept="image/*" 
                  className="hidden" 
                  onChange={(e) => handleAvatarChange(e.target.files?.[0] || null)}
                />
              </div>
              <p className="text-xs text-muted-foreground text-center mt-2">Profile Photo</p>
            </div>

            <div className="flex-1 space-y-4">
              <div>
                <Label htmlFor="fullName" className="text-base font-medium">Name</Label>
                <Input
                  id="fullName"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  placeholder="Your name"
                  className="mt-1.5"
                />
              </div>

              <div>
                <Label htmlFor="username" className="text-base font-medium">Username</Label>
                <div className="relative mt-1.5">
                  <Input
                    id="username"
                    value={profile?.username || ""}
                    readOnly
                    className="bg-muted/50"
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Your username cannot be changed after account creation.
                </p>
              </div>
            </div>
          </div>
          
          <div className="pt-4 flex justify-end">
            <Button 
              onClick={saveProfile} 
              disabled={saving}
              className="w-full sm:w-auto"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Profile Changes"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <Label htmlFor="email">Email Address</Label>
            <div className="relative mt-1.5">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                id="email"
                type="email"
                value={userData?.email || ""}
                readOnly
                className="pl-10 bg-muted/50"
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              This is the email address associated with your account.
            </p>
          </div>

          <div className="pt-4">
            <Label htmlFor="reset-password">Password</Label>
            <div className="mt-1.5">
              <Button
                variant="outline"
                onClick={handleResetPassword}
                disabled={resetPasswordLoading}
                className="w-full sm:w-auto"
              >
                {resetPasswordLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending email...
                  </>
                ) : (
                  "Send Password Reset Email"
                )}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1.5">
              You'll receive an email with instructions to reset your password.
            </p>
          </div>
        </CardContent>
      </Card>

      {!hideAccountRole && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              Account Role
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-base font-medium">Current Role</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Your account is currently set as a{" "}
                  <span className="font-semibold capitalize">{profile?.role || "student"}</span> account.
                </p>
              </div>
              <div className="bg-muted px-3 py-1.5 rounded-full text-sm font-medium capitalize">
                {profile?.role || "student"}
              </div>
            </div>
            
            <div className="pt-4">
              <Button
                onClick={() => setShowRoleSwitchDialog(true)}
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Switch to {targetRole}
              </Button>
              <p className="text-xs text-muted-foreground mt-1.5">
                Switching roles will change how you interact with the platform. You can switch back at any time.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="flex justify-end mt-8">
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="ghost" size="sm" className="text-muted-foreground">
              Delete Account
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
              <AlertDialogDescription>
                This action cannot be undone. This will permanently delete your account
                and remove all your data from our servers.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                Delete Account
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <Dialog open={showRoleSwitchDialog} onOpenChange={setShowRoleSwitchDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Switch to {targetRole} account?</DialogTitle>
            <DialogDescription>
              {targetRole === 'creator' 
                ? "You'll be able to create and publish courses as a creator."
                : "You'll return to student view where you can access your enrolled courses."}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <p className="text-sm">
              {targetRole === 'creator' 
                ? "If you haven't set up a creator profile yet, you'll be taken through the onboarding process."
                : "Your creator content will remain saved and can be accessed when you switch back."}
            </p>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleSwitchDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleRoleSwitch} 
              disabled={roleSwitchLoading}
              className="gap-2"
            >
              {roleSwitchLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Switching...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Switch to {targetRole}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
